import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import { ShoppingBag, ArrowRight, Users, Coins, Sparkles } from 'lucide-react';
import { LocalStorageService } from '../../configuration';
import { routePaths } from '../../routes/routes-path';
import {
  getPublicProduct,
  getPublicProducts,
  PublicProduct,
} from '../../api/payment/public-product.api';

const fmt = (n: number) => Number(n || 0).toLocaleString('vi-VN');

const RARITY_LABEL: Record<string, string> = {
  Common: 'Thường',
  Rare: 'Hiếm',
  Epic: 'Sử thi',
  Legendary: 'Huyền thoại',
  Mythic: 'Thần thoại',
};

/**
 * Trang sản phẩm CÔNG KHAI — đích của link tiếp thị (cab.vn/p/{id}?ref=...).
 *
 * Bố cục theo yêu cầu: NỬA TRÊN là sản phẩm mà link tiếp thị đang bán, phần dưới
 * giới thiệu Cab và vài sản phẩm khác.
 *
 * Người bấm link thường CHƯA có tài khoản Cab — cho xem sản phẩm trước, bấm Mua mới
 * yêu cầu đăng nhập. Mã giới thiệu (?ref=) đã được captureAffRef bắt ở root cho MỌI
 * trang nên hoa hồng vẫn quy đúng người; link sang sản phẩm khác vẫn giữ ?ref cho chắc.
 */
const PublicProductPage = () => {
  const { productId } = useParams<{ productId: string }>();
  const [searchParams] = useSearchParams();
  const ref = searchParams.get('ref');
  const navigate = useNavigate();

  const [product, setProduct] = useState<PublicProduct | null>(null);
  const [others, setOthers] = useState<PublicProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!productId) return;
    getPublicProduct(productId)
      .then(setProduct)
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
    getPublicProducts(productId, 6)
      .then(setOthers)
      .catch(() => setOthers([]));
  }, [productId]);

  const linkTo = (id: string) => (ref ? `/p/${id}?ref=${ref}` : `/p/${id}`);

  const handleBuy = () => {
    if (!product) return;
    const logged = !!LocalStorageService.getItem('accessToken');
    navigate(logged ? product.buyPath : routePaths.login);
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 dark:bg-dark2">
        <p className="text-sm text-slate-500">Đang tải…</p>
      </div>
    );
  }

  if (failed || !product) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-slate-50 px-4 text-center dark:bg-dark2">
        <p className="text-lg font-semibold text-slate-800 dark:text-white">
          Không tìm thấy sản phẩm
        </p>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Sản phẩm có thể đã ngừng bán hoặc đường dẫn không đúng.
        </p>
        <button
          type="button"
          onClick={() => navigate('/')}
          className="mt-2 rounded-lg bg-[#066bff] px-4 py-2 text-sm font-semibold text-white"
        >
          Về trang chủ
        </button>
      </div>
    );
  }

  const rarity = product.rarity ? RARITY_LABEL[product.rarity] || product.rarity : null;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-dark2">
      {/* ── NỬA TRÊN: sản phẩm mà link tiếp thị đang bán ── */}
      <section className="flex min-h-[60vh] items-center justify-center bg-gradient-to-b from-[#e8f4ff] to-slate-50 px-4 py-10 dark:from-slate-800 dark:to-dark2">
        <div className="w-full max-w-[460px] rounded-2xl bg-white p-6 shadow-[4px_4px_44px_rgba(0,0,0,0.08)] dark:bg-slate-800 dark:shadow-none">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700 dark:bg-slate-700 dark:text-blue-300">
              {product.category}
            </span>
            {rarity && (
              <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700 dark:bg-slate-700 dark:text-amber-300">
                {rarity}
              </span>
            )}
          </div>

          <h1 className="mt-3 break-words text-[28px] font-bold leading-9 text-[#19052f] dark:text-white">
            {product.title}
          </h1>

          <div className="mt-4 rounded-xl border border-[#48afff] px-4 py-3 text-center dark:border-[#2f6d9c] dark:bg-white/5">
            <div className="text-[30px] font-semibold leading-10 text-[#00669a] dark:text-[#48afff]">
              {fmt(product.priceCa)} Ca
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400">
              1đ = 1 Ca · tương đương {fmt(product.priceCa)}đ
            </div>
          </div>

          {!product.isActive ? (
            <p className="mt-4 rounded-lg bg-slate-100 px-3 py-2 text-center text-sm text-slate-600 dark:bg-slate-700 dark:text-slate-300">
              Sản phẩm này hiện đã ngừng bán.
            </p>
          ) : (
            <button
              type="button"
              onClick={handleBuy}
              className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-[#066bff] py-3 text-[16px] font-semibold text-white transition hover:brightness-105"
            >
              <ShoppingBag size={18} />
              Mua ngay
              <ArrowRight size={18} />
            </button>
          )}

          <p className="mt-3 text-center text-xs text-slate-500 dark:text-slate-400">
            Cần đăng nhập hoặc tạo tài khoản Cab để mua.
          </p>
        </div>
      </section>

      {/* ── PHẦN DƯỚI: giới thiệu Cab ── */}
      <section className="mx-auto max-w-[960px] px-4 py-12">
        <h2 className="text-center text-[22px] font-bold text-[#19052f] dark:text-white">
          Cab.vn là gì?
        </h2>
        <p className="mx-auto mt-2 max-w-[560px] text-center text-sm leading-6 text-slate-600 dark:text-slate-300">
          Mạng xã hội cho người sáng tạo nội dung: đăng bài, làm video, livestream và
          kiếm thu nhập trực tiếp từ người theo dõi.
        </p>

        <div className="mt-8 grid gap-4 sm:grid-cols-3">
          {[
            { Icon: Users, title: 'Kết nối người theo dõi', desc: 'Đăng bài, video, livestream và xây cộng đồng của riêng bạn.' },
            { Icon: Coins, title: 'Ca — đơn vị trong Cab', desc: 'Quy đổi 1đ = 1 Ca. Dùng để mua vật phẩm, ủng hộ người bạn thích.' },
            { Icon: Sparkles, title: 'Vật phẩm & quyền lợi', desc: 'Khung avatar, nametag, gói thành viên giúp hồ sơ của bạn nổi bật.' },
          ].map(({ Icon, title, desc }) => (
            <div
              key={title}
              className="rounded-2xl bg-white p-5 text-center shadow-[2px_2px_24px_rgba(0,0,0,0.05)] dark:bg-slate-800 dark:shadow-none"
            >
              <Icon size={22} className="mx-auto text-[#066bff]" />
              <div className="mt-2 text-sm font-semibold text-[#19052f] dark:text-white">
                {title}
              </div>
              <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── PHẦN DƯỚI: các sản phẩm khác ── */}
      {others.length > 0 && (
        <section className="mx-auto max-w-[960px] px-4 pb-16">
          <h2 className="text-center text-[22px] font-bold text-[#19052f] dark:text-white">
            Sản phẩm khác trên Cab
          </h2>

          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {others.map((o) => (
              <Link
                key={o.id}
                to={linkTo(o.id)}
                className="flex flex-col gap-2 rounded-2xl bg-white p-5 shadow-[2px_2px_24px_rgba(0,0,0,0.05)] transition hover:-translate-y-0.5 dark:bg-slate-800 dark:shadow-none"
              >
                <span className="w-fit rounded-full bg-blue-50 px-2.5 py-0.5 text-[11px] font-semibold text-blue-700 dark:bg-slate-700 dark:text-blue-300">
                  {o.category}
                </span>
                <div className="break-words text-sm font-semibold text-[#19052f] dark:text-white">
                  {o.title}
                </div>
                <div className="mt-auto text-[17px] font-semibold text-[#00669a] dark:text-[#48afff]">
                  {fmt(o.priceCa)} Ca
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default PublicProductPage;
